/**
 * OM_Simple — the WATCH and CONTINUATION marks, in one file with NO IMPORTS.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THESE ARE
 *
 * OM_Simple marks the bar where the CLN regression colour FLIPS, and nothing
 * else. Its own guide names the cost of that: "The strategy will never tell you
 * to close a position. It has no stop, no target and no time exit, so an open
 * trade rides until the colour flips back, however far that is."
 *
 * These two marks are the missing half of that sentence — not new entries, but
 * the answer to "should I keep holding this?":
 *
 *   W  WATCH        the leg you are IN is weakening. Reversal pressure AGAINST
 *                   it has crossed its threshold. Coloured and placed for the
 *                   direction the flip WOULD go, so while long it is a red W
 *                   above the bar.
 *
 *   C  CONTINUATION the leg you are IN is resuming. Continuation pressure FOR
 *                   it has crossed its threshold AND the higher timeframe
 *                   agrees. Coloured and placed for the direction already held.
 *
 * Neither is a trade. OM_Simple is always in the market, so a continuation can
 * only ever point the way the position already points.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE HAS NO IMPORTS
 *
 * The same reason `omSimpleEngine.ts` has none, and it pays off twice.
 *
 * It is frontend-only today. If CONTINUATION is ever promoted to a scan signal,
 * a zero-import file is a `cp` plus one line in `scripts/check-engine-parity.mjs`
 * rather than a rewrite.
 *
 * More usefully NOW: the calibration study that chooses the thresholds is a
 * backend script, and a zero-import module can be loaded into it through the
 * transpile seam `verifyOmClnFlip.ts` already uses for `clnCandles.ts`. That
 * means the study measures THE SHIPPING BYTES rather than a second copy of the
 * arithmetic, which removes the whole class of "the study measured something
 * the chart does not compute".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THE FORMULAS COME FROM, AND THE THREE DELIBERATE DEVIATIONS
 *
 * The feature block is the TradingView reference "LR MTF Reversal/Continuation",
 * ported verbatim: slope and acceleration of the regression basis, slope decay
 * against its own 10-bar average, extension from the basis in ATR, a faded
 * memory of the last close/basis cross, a 3-bar structure break, a confirmed
 * 5-bar pivot break, and a volume block capped at ±20% that can never set a
 * score on its own.
 *
 * 1. THE THRESHOLDS ARE NOT PORTED. The reference uses watch 55 / confirm 70.
 *    Measured on this app's data — 48 symbols, 4H, 2019-2026 — reversal
 *    pressure reached 70 on only FIFTY FLIP BARS in six years, on one symbol.
 *    (That figure counts flips, not bars: W fires mid-leg, a different and much
 *    larger population, which is why the shipped numbers come from an all-bar
 *    sweep instead.) The reference also runs a 21-bar regression while
 *    OM_Simple runs 50, which flattens per-bar slope and pushes every score
 *    lower still — so a faithful port would draw marks that never appear.
 *
 * 2. ATR IS A SIMPLE 14-BAR AVERAGE OF TRUE RANGE, not Pine's Wilder `ta.atr`.
 *    It matches `backend/src/scripts/om/ablateOmSimple.ts`, which produced the
 *    measurements this module is calibrated against. The chart agreeing with
 *    its own study matters more here than matching Pine to the last decimal.
 *
 * 3. A SERIES WITH NO VOLUME GETS NO VOLUME MODIFIER AT ALL. Reading absent
 *    participation as neutral participation is not neutral: with `partDir` and
 *    `vwapNorm` pinned at 0 the modifier lands at 0.91, quietly docking every
 *    score on a volume-less feed by 9% and shifting it against a calibrated
 *    threshold. `hasVolume` is therefore explicit, and false means the factor
 *    is exactly 1.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE DIRECTION COMES FROM `pos` AND NOT FROM THE CANDLE COLOUR
 *
 * `computeOmSimpleMarks` takes the running POSITION from the engine, not the
 * CLN colour. With `suppressVolContradiction` on, the two diverge: a flip can
 * be suppressed, leaving the colour changed while the position has not. Keying
 * off the colour there would put a W on the chart that contradicts the B/S
 * arrow beside it, which is worse than no W at all.
 *
 * CAUSALITY. Every window is trailing and the 5-bar pivot is only read five
 * bars after it forms, so truncating the series never changes an earlier score.
 * Callers pass CLOSED bars only, and the HTF colour must come from the last
 * CLOSED higher-timeframe bar.
 */

/** The OHLCV fields this file reads. `CandleRow` is a structural superset. */
export interface OmSimplePressureBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

/** The four directional pressures for one bar, each 0-100. */
export interface OmSimplePressureRow {
  time: number;
  revLong: number;
  revShort: number;
  contLong: number;
  contShort: number;
}

export type OmSimpleDir = 'LONG' | 'SHORT';

/** One bar's marks. Both null on the overwhelming majority of bars. */
export interface OmSimpleMarkRow {
  time: number;
  /** Rising-edge WATCH, pointing where the flip WOULD go. */
  watch: OmSimpleDir | null;
  /** Rising-edge CONTINUATION, pointing the way the position already points. */
  continuation: OmSimpleDir | null;
  /** The score that fired it, so a tooltip can show what crossed and by how much. */
  watchScore: number | null;
  continuationScore: number | null;
}

/**
 * Shipped thresholds — NOT the reference's 55 / 70. See deviation 1 above.
 *
 * MEASURED, `calibrateOmPressure.ts`, 48 symbols / 4H / cln 50 / 487,483 bars /
 * 47,460 legs / 2,669 symbol-months. Per 100 bars a chart shows:
 *
 *   W at 45   3.5 marks   the against-leg score's 95th percentile is 43.1
 *   C at 55   5.0 marks   the for-leg score's 90th percentile is 63.3
 *
 * WHERE PINE'S NUMBERS ACTUALLY SIT ON THIS DATA, which is the whole argument
 * for not copying them: 55 is the **98.2nd percentile** of the against-leg
 * score, and that score never exceeds 73.6 in six years — so Pine's confirm
 * tier of 70 is close to unreachable for a reversal here. 70 is the 95.9th
 * percentile of the for-leg score.
 *
 * WHY 55 FOR C RATHER THAN SOMETHING LOWER. Between 30 and 60 the C rate is
 * almost flat (8.3-9.8 per symbol-month) because the re-arm band moves with the
 * threshold, so the choice is not a density choice — it is a selectivity one.
 * 55 puts a C at roughly the 85th percentile of the for-leg score while keeping
 * the density readable. A per-leg target of ~1 C was fixed in advance and is
 * NOT reachable at any threshold (0.55/leg is the maximum, at 30); lowering the
 * threshold to chase it would buy nothing but noise, so the target was missed
 * and the density criterion decided instead. Said here rather than quietly
 * dropped.
 *
 * DOES "WATCH" EARN ITS NAME? Measured bar-for-bar at these settings, a flip
 * follows within 10 bars **70%** of the time after a W against a **45%** base
 * rate — a lift of 1.54x, and it holds in all four leg-age strata (1.33x-1.84x),
 * so leg length is not doing the work. Reported, never tuned against: fitting a
 * chart annotation to what happens next would quietly make it a trade.
 *
 * CALIBRATED AT 4H. The density at other intervals is not measured; the engine's
 * own default length was measured at 7H, which is not in the study's bar cache.
 * Re-run the study before trusting these numbers on a very different timeframe.
 */
export const OM_SIMPLE_WATCH_THRESHOLD = 45;
export const OM_SIMPLE_CONTINUATION_THRESHOLD = 55;

/**
 * Hysteresis. Once a mark fires it cannot fire again until the score falls this
 * many points back below the threshold.
 *
 * This is the stateless substitute for the reference's persistent state
 * machine. Without it a score hovering on the threshold prints a mark on every
 * other bar, and the chart becomes less readable rather than more informative.
 */
export const OM_SIMPLE_REARM_DROP = 10;

/** Bars after a flip before a CONTINUATION may print — below this it is just
 *  restating the entry arrow that is already on the bar. */
export const OM_SIMPLE_CONT_MIN_BARS = 3;

export interface OmSimpleMarkOptions {
  watchThreshold?: number;
  continuationThreshold?: number;
  rearmDrop?: number;
  continuationMinBars?: number;
  /** First bar index that may carry a mark — the caller's warm-up floor. */
  warmFrom?: number;
  /** Require the higher timeframe to agree before a CONTINUATION prints.
   *  Default true; a null HTF colour then BLOCKS rather than passes. */
  requireHtfAgreement?: boolean;
}

// ── small pure helpers ──────────────────────────────────────────────────────

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** x / (x + k) — the reference's squash, mapping [0, inf) onto [0, 1). */
function squash01(x: number, k: number): number {
  const xx = x > 0 ? x : 0;
  const kk = k > 1e-10 ? k : 1e-10;
  return xx / (xx + kk);
}

/** Signed squash: magnitude through `squash01`, sign preserved. */
function squashSigned(x: number, k: number): number {
  const s = squash01(x < 0 ? -x : x, k);
  return x >= 0 ? s : -s;
}

/** Trailing simple average of `a` ending at `i`. NaN until the window is full. */
function smaAt(a: number[], i: number, n: number): number {
  if (i + 1 < n) return NaN;
  let s = 0;
  for (let k = i - n + 1; k <= i; k++) s += a[k];
  return s / n;
}

// ── scoring ─────────────────────────────────────────────────────────────────

/**
 * The four directional pressures, per bar.
 *
 * `basis` is the regression of the closes — `ClnBar.lrClose`, at the STRATEGY's
 * regression length, not the chart's drawing length. Pass the same array
 * OM_Simple itself evaluates or the scores describe a different curve from the
 * one the arrows came from.
 */
export function computeOmSimplePressure(
  bars: ReadonlyArray<OmSimplePressureBar>,
  basis: ReadonlyArray<number>,
): OmSimplePressureRow[] {
  const out: OmSimplePressureRow[] = [];
  const n = Math.min(bars ? bars.length : 0, basis ? basis.length : 0);
  if (!n) return out;

  // True range, then ATR as a simple 14-bar mean. See deviation 2.
  const tr: number[] = [];
  const atr: number[] = [];
  for (let i = 0; i < n; i++) {
    const b = bars[i];
    if (i === 0) {
      tr.push(b.high - b.low);
    } else {
      const pc = bars[i - 1].close;
      const a1 = b.high - b.low;
      const a2 = b.high > pc ? b.high - pc : pc - b.high;
      const a3 = b.low > pc ? b.low - pc : pc - b.low;
      tr.push(a1 > a2 ? (a1 > a3 ? a1 : a3) : (a2 > a3 ? a2 : a3));
    }
    atr.push(smaAt(tr, i, 14));
  }

  const slope: number[] = [];
  const slopeMag: number[] = [];
  for (let i = 0; i < n; i++) {
    const s = i === 0 ? 0 : basis[i] - basis[i - 1];
    slope.push(s);
    slopeMag.push(s < 0 ? -s : s);
  }

  // Volume. `hasVolume` is decided ONCE for the series: a feed either carries
  // participation or it does not, and a single zero-volume bar inside a real
  // feed is data, not an absent feed.
  let volCount = 0;
  const vols: number[] = [];
  for (let i = 0; i < n; i++) {
    const v = Number(bars[i].volume);
    const ok = Number.isFinite(v) && v > 0;
    if (ok) volCount++;
    vols.push(ok ? v : 0);
  }
  const hasVolume = volCount > n / 2;

  // Rolling 20-bar volume-weighted price.
  const pvWin: number[] = [];
  const vWin: number[] = [];
  let pvSum = 0;
  let vSum = 0;
  for (let i = 0; i < n; i++) {
    const b = bars[i];
    pvSum += ((b.high + b.low + b.close) / 3) * vols[i];
    vSum += vols[i];
    if (i >= 20) {
      const o = bars[i - 20];
      pvSum -= ((o.high + o.low + o.close) / 3) * vols[i - 20];
      vSum -= vols[i - 20];
    }
    pvWin.push(pvSum);
    vWin.push(vSum);
  }

  // Last CONFIRMED 5-bar pivot, as known at each bar. A pivot at `mid` is only
  // readable once its right-hand side has closed, which is what keeps this causal.
  const P = 5;
  const pivHi: number[] = [];
  const pivLo: number[] = [];
  let lastHi = NaN;
  let lastLo = NaN;
  for (let i = 0; i < n; i++) {
    const mid = i - P;
    if (mid >= P) {
      let isHi = true;
      let isLo = true;
      for (let k = mid - P; k <= mid + P; k++) {
        if (k === mid) continue;
        if (bars[k].high >= bars[mid].high) isHi = false;
        if (bars[k].low <= bars[mid].low) isLo = false;
      }
      if (isHi) lastHi = bars[mid].high;
      if (isLo) lastLo = bars[mid].low;
    }
    pivHi.push(lastHi);
    pivLo.push(lastLo);
  }

  let barsSinceCross = 999999;
  let crossDir = 0;

  for (let i = 0; i < n; i++) {
    const b = bars[i];
    const a = atr[i];
    const hasAtr = Number.isFinite(a) && a > 0;

    if (i > 0) {
      const wasBelow = bars[i - 1].close <= basis[i - 1];
      const wasAbove = bars[i - 1].close >= basis[i - 1];
      if (wasBelow && b.close > basis[i]) { barsSinceCross = 0; crossDir = 1; }
      else if (wasAbove && b.close < basis[i]) { barsSinceCross = 0; crossDir = -1; }
      else barsSinceCross++;
    }
    const rawVsBasis = crossDir * Math.max(0, 1 - barsSinceCross / 8);

    const slopeNorm = hasAtr ? squashSigned(slope[i], 0.15 * a) : 0;
    const accelNorm = hasAtr && i > 0 ? squashSigned(slope[i] - slope[i - 1], 0.08 * a) : 0;
    const extNorm = hasAtr ? squashSigned((b.close - basis[i]) / a, 1.5) : 0;

    const magAvg = smaAt(slopeMag, i, 10);
    const slopeDecay = !Number.isFinite(magAvg) || magAvg <= 1e-10
      ? 0 : clamp01(1 - slopeMag[i] / magAvg);

    let hh3 = -Infinity;
    let ll3 = Infinity;
    for (let k = i - 3 > 0 ? i - 3 : 0; k <= i - 1; k++) {
      if (bars[k].high > hh3) hh3 = bars[k].high;
      if (bars[k].low < ll3) ll3 = bars[k].low;
    }
    const earlyStruct = i >= 4 ? (b.close > hh3 ? 1 : b.close < ll3 ? -1 : 0) : 0;
    const confStruct = Number.isFinite(pivHi[i]) && b.close > pivHi[i] ? 1
      : Number.isFinite(pivLo[i]) && b.close < pivLo[i] ? -1 : 0;
    const bullStruct = Math.max(0, Math.max(earlyStruct, confStruct));
    const bearStruct = Math.max(0, Math.max(-earlyStruct, -confStruct));

    let freshHigh = 0;
    let freshLow = 0;
    if (i >= 11) {
      let pHi = -Infinity;
      let pLo = Infinity;
      for (let k = i - 10; k <= i - 1; k++) {
        if (bars[k].close > pHi) pHi = bars[k].close;
        if (bars[k].close < pLo) pLo = bars[k].close;
      }
      freshHigh = b.close > pHi ? 1 : 0;
      freshLow = b.close < pLo ? 1 : 0;
    }

    // Volume block — capped at ±20%, and ENTIRELY ABSENT on a feed without
    // volume rather than silently landing at 0.91. See deviation 3.
    let fRevLong = 1;
    let fRevShort = 1;
    let fContLong = 1;
    let fContShort = 1;
    if (hasVolume) {
      const vAvg = smaAt(vols, i, 20);
      if (Number.isFinite(vAvg) && vAvg > 0) {
        const relVol = vols[i] / vAvg;
        const bodyRange = Math.max(b.high - b.low, 1e-12);
        const partDir = squashSigned(((b.close - b.open) / bodyRange) * relVol, 1);
        const vwp = vWin[i] > 0 ? pvWin[i] / vWin[i] : (b.high + b.low + b.close) / 3;
        const vwapNorm = hasAtr ? squashSigned((b.close - vwp) / a, 1) : 0;
        fRevLong = 0.80 + 0.20 * clamp01(0.55 * (1 - Math.max(-partDir, 0)) + 0.25 * Math.max(vwapNorm, 0));
        fRevShort = 0.80 + 0.20 * clamp01(0.55 * (1 - Math.max(partDir, 0)) + 0.25 * Math.max(-vwapNorm, 0));
        fContLong = 0.80 + 0.20 * clamp01(0.65 * Math.max(partDir, 0) + 0.35 * Math.max(vwapNorm, 0));
        fContShort = 0.80 + 0.20 * clamp01(0.65 * Math.max(-partDir, 0) + 0.35 * Math.max(-vwapNorm, 0));
      }
    }

    const revLongRaw = 0.25 * slopeDecay + 0.25 * Math.max(accelNorm, 0)
      + 0.15 * Math.max(-extNorm, 0) + 0.15 * Math.max(rawVsBasis, 0) + 0.20 * bullStruct;
    const revShortRaw = 0.25 * slopeDecay + 0.25 * Math.max(-accelNorm, 0)
      + 0.15 * Math.max(extNorm, 0) + 0.15 * Math.max(-rawVsBasis, 0) + 0.20 * bearStruct;
    const contLongRaw = 0.30 * Math.max(slopeNorm, 0) + 0.25 * Math.max(accelNorm, 0)
      + 0.15 * freshHigh + 0.30 * bullStruct;
    const contShortRaw = 0.30 * Math.max(-slopeNorm, 0) + 0.25 * Math.max(-accelNorm, 0)
      + 0.15 * freshLow + 0.30 * bearStruct;

    const r1 = Math.round(clamp01(revLongRaw * fRevLong) * 1000) / 10;
    const r2 = Math.round(clamp01(revShortRaw * fRevShort) * 1000) / 10;
    const c1 = Math.round(clamp01(contLongRaw * fContLong) * 1000) / 10;
    const c2 = Math.round(clamp01(contShortRaw * fContShort) * 1000) / 10;

    out.push({ time: bars[i].time, revLong: r1, revShort: r2, contLong: c1, contShort: c2 });
  }

  return out;
}

// ── marks ───────────────────────────────────────────────────────────────────

/**
 * Turn the pressures into at most one W and one C per bar.
 *
 * `pos[i]` is the running position AFTER bar i — `OmSimpleStep.pos`. It, not the
 * candle colour, decides which score is "for" the leg and which is "against"
 * it; see the header.
 *
 * `isSignalBar[i]` is true where the engine emitted a BUY or SHORT. Those bars
 * already carry an arrow and are left alone entirely.
 *
 * `htfUp[i]` is the colour of the last CLOSED higher-timeframe CLN bar at bar i,
 * or null where there is none. With `requireHtfAgreement` (the default) a null
 * BLOCKS a continuation rather than passing it — an ungated continuation is a
 * different claim from a confirmed one, and quietly substituting the first for
 * the second is how a mark stops meaning what its tooltip says.
 */
export function computeOmSimpleMarks(
  pressure: ReadonlyArray<OmSimplePressureRow>,
  pos: ReadonlyArray<'LONG' | 'SHORT' | 'FLAT'>,
  isSignalBar: ReadonlyArray<boolean>,
  htfUp: ReadonlyArray<boolean | null> | null,
  opts: OmSimpleMarkOptions = {},
): OmSimpleMarkRow[] {
  const out: OmSimpleMarkRow[] = [];
  const n = Math.min(pressure ? pressure.length : 0, pos ? pos.length : 0);
  if (!n) return out;

  const num = (v: any, d: number) => (Number.isFinite(Number(v)) ? Number(v) : d);
  const wThr = num(opts.watchThreshold, OM_SIMPLE_WATCH_THRESHOLD);
  const cThr = num(opts.continuationThreshold, OM_SIMPLE_CONTINUATION_THRESHOLD);
  const rearm = num(opts.rearmDrop, OM_SIMPLE_REARM_DROP);
  const minBars = num(opts.continuationMinBars, OM_SIMPLE_CONT_MIN_BARS);
  const warmFrom = Math.max(0, Math.round(num(opts.warmFrom, 0)));
  const gateHtf = opts.requireHtfAgreement !== false;

  // Armed/disarmed rather than a bare rising edge: a score hovering on the
  // threshold would otherwise print a mark every other bar.
  let watchArmed = true;
  let contArmed = true;
  let barsSinceFlip = 0;

  for (let i = 0; i < n; i++) {
    const p = pressure[i];
    const here = pos[i];
    const signalled = !!(isSignalBar && isSignalBar[i]);

    if (signalled) {
      // A new leg: re-arm both, and start counting again.
      barsSinceFlip = 0;
      watchArmed = true;
      contArmed = true;
      out.push({ time: p.time, watch: null, continuation: null, watchScore: null, continuationScore: null });
      continue;
    }
    barsSinceFlip++;

    let watch: OmSimpleDir | null = null;
    let continuation: OmSimpleDir | null = null;
    let watchScore: number | null = null;
    let contScore: number | null = null;

    if (i >= warmFrom && here !== 'FLAT') {
      const long = here === 'LONG';
      // AGAINST the leg for a watch, FOR the leg for a continuation.
      const against = long ? p.revShort : p.revLong;
      const forLeg = long ? p.contLong : p.contShort;

      if (watchArmed && against >= wThr) {
        watch = long ? 'SHORT' : 'LONG';
        watchScore = against;
        watchArmed = false;
      } else if (!watchArmed && against < wThr - rearm) {
        watchArmed = true;
      }

      const h = htfUp && i < htfUp.length ? htfUp[i] : null;
      const htfOk = !gateHtf || (h !== null && h === long);
      if (contArmed && forLeg >= cThr && barsSinceFlip >= minBars && htfOk) {
        continuation = long ? 'LONG' : 'SHORT';
        contScore = forLeg;
        contArmed = false;
      } else if (!contArmed && forLeg < cThr - rearm) {
        contArmed = true;
      }
    }

    out.push({ time: p.time, watch, continuation, watchScore, continuationScore: contScore });
  }

  return out;
}
